import { formatPhoneNumber } from "@/utils/formatUtils";
import { toDate } from "@/utils/utils";
import t, { DEFAULT_LOCALE } from "../../app/i18n";
import { ContactType, TopAgentType } from "../../common/enums";
import { InstitutionType } from "../institution/enums";
import {
  AgentCompetenceLevel,
  AgentCompetenceType,
  AgentStatus,
  AgentType,
  FinancialSector,
  SecondaryAgentType,
} from "./enums";
import type {
  Agent,
  AgentBase,
  AgentBrokerNumber,
  AgentCompetence,
  AgentCompletedEducation,
  AgentDirectorate,
  AgentDirectorateBase,
  AgentEducation,
  AgentEducationBase,
  AgentLicense,
  AgentMentoring,
  CreateUpdateAgent,
  CreateUpdateAgentCompletedEducation,
  CreateUpdateAgentDirectorate,
  CreateUpdateAgentEducation,
  CreateUpdateAgentMentoring,
  CreateUpdateLegalAgent,
  CreateUpdateLegalSecondaryAgent,
  CreateUpdateNaturalAgent,
  CreateUpdateNaturalSecondaryAgent,
  CreateUpdateSecondaryAgent,
  CreateUpdateSelfEmployedAgent,
  CreateUpdateSelfEmployedSecondaryAgent,
  LegalAgent,
  LegalSecondaryAgent,
  NaturalAgent,
  NaturalSecondaryAgent,
  SecondaryAgent,
  SelfEmployedAgent,
  SelfEmployedSecondaryAgent,
} from "./types";

const byEnumOrder = (enumObj: Record<string, string>, a?: string, b?: string): number =>
  Object.keys(enumObj).indexOf(a ?? "") - Object.keys(enumObj).indexOf(b ?? "");

const byLabel = (a?: string, b?: string): number =>
  (a ?? "").localeCompare(b ?? "", DEFAULT_LOCALE, { sensitivity: "accent" });

export const sortAgentCompetences = (competences: AgentCompetence[]): AgentCompetence[] => {
  return [...competences].sort(
    (a, b) =>
      byEnumOrder(AgentCompetenceType, a.type, b.type) ||
      byEnumOrder(FinancialSector, a.sector, b.sector) ||
      byEnumOrder(AgentCompetenceLevel, a.level, b.level) ||
      byLabel(a.label, b.label),
  );
};

export const sortAgentLicenses = (licenses: AgentLicense[]): AgentLicense[] => {
  return [...licenses].sort(
    (a, b) =>
      byEnumOrder(FinancialSector, a.sector, b.sector) ||
      toDate(a.registrationProposalDate)?.diff(toDate(b.registrationProposalDate)) ||
      byLabel(a.value, b.value),
  );
};

export const sortAgentBrokerNumbers = (brokerNumbers: AgentBrokerNumber[]): AgentBrokerNumber[] => {
  return [...brokerNumbers].sort(
    (a, b) =>
      byEnumOrder(InstitutionType, a.institution.type, b.institution.type) ||
      byLabel(a.institution.name, b.institution.name) ||
      toDate(a.validFrom)?.diff(toDate(b.validFrom)) ||
      toDate(a.validTo)?.diff(toDate(b.validTo)) ||
      byLabel(a.value, b.value),
  );
};

export const convertAgentToCreateUpdateAgent = <T extends Agent>(agent: T): CreateUpdateAgent => {
  const agentBase = {
    parentId: agent.parent ? agent.parent.id : undefined,
    phone: formatPhoneNumber(agent.phone),
    contacts: agent.contacts.map((contact) => ({
      ...contact,
      value: contact.type === ContactType.PHONE_NUMBER ? formatPhoneNumber(contact.value) : contact.value,
    })),
    directorateId: agent.directorate?.id,
    structureIdNumber: formatAgentStructureIdNumber(agent.structureIdNumber),
  } as Partial<CreateUpdateAgent>;

  const { profilePicture, parent, attachments, historyRecords, createdBy, ...data } = agent;

  if (isLegalAgent(agent)) {
    return {
      ...data,
      ...agentBase,
      representativeIds: agent.representatives.map((r) => r.id),
    } as CreateUpdateLegalAgent;
  } else if (isSelfEmployedAgent(agent)) {
    return {
      ...data,
      ...agentBase,
    } as CreateUpdateSelfEmployedAgent;
  } else {
    return {
      ...data,
      ...agentBase,
    } as CreateUpdateNaturalAgent;
  }
};

export const createPartialCreateUpdateAgent = (agent: Partial<Agent>): Partial<CreateUpdateAgent> => {
  const { profilePicture, parent, attachments, historyRecords, createdBy, ...data } = agent;

  let result: Partial<CreateUpdateAgent> = { ...data };

  if (data.type === AgentType.LEGAL) {
    result = {
      ...result,
      representativeIds: data.representatives?.map((r) => r.id),
    } as Partial<CreateUpdateLegalAgent>;
  }

  return {
    ...result,
    parentId: agent.parent ? agent.parent.id : undefined,
    phone: agent.phone ? formatPhoneNumber(agent.phone) : undefined,
    contacts: agent.contacts?.map((contact) => ({
      ...contact,
      value: contact.type === ContactType.PHONE_NUMBER ? formatPhoneNumber(contact.value) : contact.value,
    })),
    directorateId: agent.directorate?.id,
    structureIdNumber: formatAgentStructureIdNumber(agent.structureIdNumber),
  };
};

export const convertAgentDirectorateToCreateUpdateObject = (
  directorate: AgentDirectorate,
): CreateUpdateAgentDirectorate => {
  const { createdAt, updatedAt, id, headAgent, ...data } = directorate;

  return { ...data, headAgentId: headAgent.id };
};

export const convertAgentEducationToCreateUpdateObject = (education: AgentEducation): CreateUpdateAgentEducation => {
  const { createdAt, updatedAt, id, ...data } = education;

  return data;
};

export const convertAgentCompletedEducationToCreateUpdateObject = (
  completedEducation: AgentCompletedEducation,
): CreateUpdateAgentCompletedEducation => {
  const { createdAt, updatedAt, id, education, ...data } = completedEducation;

  return { ...data, educationId: education?.id };
};

export const convertAgentMentoringToCreateUpdateObject = (mentoring: AgentMentoring): CreateUpdateAgentMentoring => {
  const { createdAt, updatedAt, contractsCount, mentor, adept, id, ...data } = mentoring;

  return { ...data, mentorId: mentor.id, adeptId: adept.id };
};

export const isNaturalAgent = (agent: Agent): agent is NaturalAgent => agent.type === AgentType.NATURAL;

export const isSelfEmployedAgent = (agent: Agent): agent is SelfEmployedAgent => agent.type === AgentType.SELF_EMPLOYED;

export const isLegalAgent = (agent: Agent): agent is LegalAgent => agent.type === AgentType.LEGAL;

export const isAgentTipper = <T extends AgentBase>(
  agent: T,
): agent is T & { status: AgentStatus.TIPPER_WITH_CONTRACT | AgentStatus.TIPPER_WITHOUT_CONTRACT } =>
  agent.status === AgentStatus.TIPPER_WITH_CONTRACT || agent.status === AgentStatus.TIPPER_WITHOUT_CONTRACT;

export const formatAgentDirectorateName = (directorate: AgentDirectorateBase) => {
  return `${directorate.code} - ${directorate.name}`;
};

export const formatAgentEducationName = (education: AgentEducationBase) => {
  return `${education.code} - ${education.name}`;
};

export const formatAgentStructureIdNumber = (
  structureIdNumber?: AgentBase["structureIdNumber"],
): string | undefined => {
  return structureIdNumber ? structureIdNumber.toString().padStart(3, "0") : undefined;
};

export const formatAgentShortSystemIdNumber = (agent?: AgentBase): string | undefined => {
  return agent ? agent.systemIdNumber?.toString().padStart(5, "0") : undefined;
};

export const formatAgentSystemIdNumber = (agent?: AgentBase): string | undefined => {
  if (agent) {
    const structureId = formatAgentStructureIdNumber(agent.structureIdNumber);
    const systemId = formatAgentShortSystemIdNumber(agent);
    return structureId ? structureId + systemId : systemId;
  }

  return undefined;
};

export const formatAgentIdNumber = (agent?: AgentBase): string | undefined => {
  return agent
    ? agent.topAgentType === TopAgentType.PLUS
      ? agent.partnerIdNumber
      : formatAgentSystemIdNumber(agent)
    : undefined;
};

export const formatAgentName = (agent?: AgentBase, showDeactivatedLabel: boolean = true): string | undefined => {
  if (agent) {
    const additionalInfo = [isAgentTipper(agent) ? "T" : undefined, formatAgentIdNumber(agent)]
      .filter((info) => !!info)
      .join(" | ");

    return (
      agent.displayName +
      (additionalInfo ? ` (${additionalInfo})` : "") +
      (agent.deactivated && showDeactivatedLabel ? ` | ${t("agent.helpers.deactivatedAgentShort")}` : "")
    );
  }

  return;
};

export const formatAgentAggregatedName = (agent?: AgentBase): string | undefined => {
  if (agent) {
    const additionalInfo = [formatAgentIdNumber(agent), isAgentTipper(agent) ? "T" : undefined]
      .filter((info) => !!info)
      .join(" | ");
    return agent.aggregatedName + (additionalInfo ? ` (${additionalInfo})` : "");
  }

  return;
};

export const AgentTreeColumnsWidth = {
  AGENT: 445,
  ID_NUMBER: 100,
  IDENTIFIER: 110,
  LEVEL: 100,
  RANK: 135,
  TIPPER: 60,
  POINTS: 110,
  ACTIONS: 90,
};

export const isLoanSpecialistDisabled = (status?: AgentStatus, sectors?: FinancialSector[]): boolean =>
  !status ||
  status === AgentStatus.TIPPER_WITH_CONTRACT ||
  status === AgentStatus.TIPPER_WITHOUT_CONTRACT ||
  !sectors?.length;

export const isRealtySpecialistDisabled = (status?: AgentStatus, sectors?: FinancialSector[]): boolean =>
  !status ||
  status === AgentStatus.TIPPER_WITH_CONTRACT ||
  status === AgentStatus.TIPPER_WITHOUT_CONTRACT ||
  !sectors?.includes(FinancialSector.INSURANCES_OR_REINSURANCES);

export const isNaturalSecondaryAgent = (agent: SecondaryAgent): agent is NaturalSecondaryAgent =>
  agent.type === SecondaryAgentType.NATURAL;

export const isSelfEmployedSecondaryAgent = (agent: SecondaryAgent): agent is SelfEmployedSecondaryAgent =>
  agent.type === SecondaryAgentType.SELF_EMPLOYED;

export const isLegalSecondaryAgent = (agent: SecondaryAgent): agent is LegalSecondaryAgent =>
  agent.type === SecondaryAgentType.LEGAL;

export const isPartialLegalSecondaryAgent = (agent: Partial<SecondaryAgent>): agent is Partial<LegalSecondaryAgent> =>
  agent.type === SecondaryAgentType.LEGAL;

export const convertSecondaryAgentToCreateUpdateObject = (
  secondaryAgent: SecondaryAgent,
): CreateUpdateSecondaryAgent => {
  if (isLegalSecondaryAgent(secondaryAgent)) {
    const {
      id,
      createdAt,
      updatedAt,
      aggregatedName,
      displayName,
      identifier,
      historyRecords,
      representatives,
      ...data
    } = secondaryAgent;

    return {
      ...data,
      representatives: representatives.map((representative) => {
        const { id, createdAt, updatedAt, aggregatedName, displayName, ...repData } = representative;
        return repData;
      }),
    };
  } else {
    const { id, createdAt, updatedAt, aggregatedName, displayName, identifier, historyRecords, ...data } =
      secondaryAgent;

    return data;
  }
};

export const convertPartialAgentToPartialCreateUpdateSecondaryAgent = (
  agent: Partial<Agent>,
): Partial<CreateUpdateSecondaryAgent> => {
  // Remove common Agent-specific fields first
  const {
    id,
    createdAt,
    updatedAt,
    aggregatedName,
    displayName,
    identifier,
    structureIdNumber,
    systemIdNumber,
    partnerIdNumber,
    accessTreePath,
    accessTreeSortPath,
    topAgentType,
    status,
    secondaryStatus,
    statusStartDate,
    statusEndDate,
    secondaryStatusStartDate,
    secondaryStatusEndDate,
    canBeGainer,
    deactivated,
    sectors,
    nbsRegistrationNumber,
    realtySpecialist,
    loanSpecialist,
    parent,
    directorate,
    profilePicture,
    createdBy,
    historyRecords,
    attachments,
    type,
    ...restData
  } = agent;

  if (type === AgentType.LEGAL) {
    const { representatives, ...commonData } = restData as Partial<LegalAgent>;

    return {
      ...commonData,
      type: SecondaryAgentType.LEGAL,
      representatives: representatives?.length
        ? representatives.map((rep) => ({ personalIdentificationNumber: rep.identifier }))
        : [{}],
    } as Partial<CreateUpdateLegalSecondaryAgent>;
  } else if (type === AgentType.SELF_EMPLOYED) {
    const { representedAgents, ...commonData } = restData as Partial<SelfEmployedAgent>;

    return {
      ...commonData,
      type: SecondaryAgentType.SELF_EMPLOYED,
    } as Partial<CreateUpdateSelfEmployedSecondaryAgent>;
  } else {
    const { representedAgents, ...commonData } = restData as Partial<NaturalAgent>;

    return {
      ...commonData,
      type: SecondaryAgentType.NATURAL,
    } as Partial<CreateUpdateNaturalSecondaryAgent>;
  }
};

export const convertSecondaryAgentTypeToAgentType = (type: SecondaryAgentType): AgentType => {
  const convertObject: Record<SecondaryAgentType, AgentType> = {
    [SecondaryAgentType.LEGAL]: AgentType.LEGAL,
    [SecondaryAgentType.SELF_EMPLOYED]: AgentType.SELF_EMPLOYED,
    [SecondaryAgentType.NATURAL]: AgentType.NATURAL,
  };

  return convertObject[type];
};

export const convertDtypeToAgentType = (dtype: string) => {
  switch (dtype) {
    case "NaturalAgent":
    case "NaturalSecondaryAgent":
      return AgentType.NATURAL;
    case "SelfEmployedAgent":
    case "SelfEmployedSecondaryAgent":
      return AgentType.SELF_EMPLOYED;
    case "LegalAgent":
    case "LegalSecondaryAgent":
      return AgentType.LEGAL;
    default:
      return;
  }
};
